import "../variables.module.css";

import classnames from "classnames";

import { TypeaheadKeyboardShortcutBanner } from "@mirage/education/TypeaheadKeyboardShortcutBanner";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import {
  getVisibleShortcuts,
  ShortcutKey,
  showAddToStackShortcut,
  showCopyLinkShortcut,
  showNewShortcut,
  showSummarizeShortcut,
  type ShortcutConfig,
} from "@mirage/mosaics/SearchBarWithTypeahead/util/util";
import { useAtomValue } from "jotai";
import { ModeAtom } from "../../atoms/mode";
import { useOmniboxMotionFlag } from "../../hooks/useOmniboxMotion";
import style from "./DropdownBanner.module.css";

// We can only show 4 shortcuts at a time due to space constraints
const MAX_SHORTCUTS = 4;

export const DropdownBanner = ({
  isDarwin,
  results,
  selectedIdx,
  isTypeaheadOpen,
  isBannerContentEnabled,
}: {
  isDarwin: boolean;
  results: TypeaheadResult[];
  selectedIdx: number;
  isTypeaheadOpen: boolean;
  isBannerContentEnabled: boolean;
}) => {
  const omniboxScopedMode = useAtomValue(ModeAtom);
  const { getMotionVersionFor } = useOmniboxMotionFlag();
  const expandMotionVersion = getMotionVersionFor("expand");

  const selectedResult = results[selectedIdx];

  const shortcutConfigs: ShortcutConfig[] = [
    {
      key: ShortcutKey.Summarize,
      priority: 1,
      isVisible: showSummarizeShortcut(selectedResult),
    },
    {
      key: ShortcutKey.AddToStack,
      priority: 1,
      isVisible: showAddToStackShortcut(selectedResult),
    },
    {
      key: ShortcutKey.CopyLink,
      priority: 1,
      isVisible: showCopyLinkShortcut(true, selectedResult),
    },

    // These two shortcuts are more generalized and thus are lower priority.
    // We don't want these to show in place of more specific shortcuts when results are selected
    {
      key: ShortcutKey.AppShortcut,
      priority: 2,
      isVisible: true,
    },
    {
      key: ShortcutKey.NewShortcut,
      priority: 3,
      isVisible: showNewShortcut(true, omniboxScopedMode),
    },
  ];

  // Get the shortcuts that should be visible based on priority
  const visibleShortcuts = getVisibleShortcuts(shortcutConfigs, MAX_SHORTCUTS);

  return (
    <div
      className={classnames(style.dropdownBanner, {
        [style.isExpandMotionV1]: expandMotionVersion === "V1",
        [style.isTypeaheadOpen]: isTypeaheadOpen,
      })}
    >
      <TypeaheadKeyboardShortcutBanner
        isDarwin={isDarwin}
        isBannerContentEnabled={isBannerContentEnabled}
        showAddToStackShortcut={visibleShortcuts.has(ShortcutKey.AddToStack)}
        showSummarizeShortcut={visibleShortcuts.has(ShortcutKey.Summarize)}
        showCopyLink={visibleShortcuts.has(ShortcutKey.CopyLink)}
        showAppShortcut={visibleShortcuts.has(ShortcutKey.AppShortcut)}
        showNewShortcut={visibleShortcuts.has(ShortcutKey.NewShortcut)}
      />
    </div>
  );
};
